import { type FC, useEffect, useState } from "react";

import { areReferencedNodeValuesEqual } from "@doitintl/cloudflow-commons";
import { ModelType, NodeTransformationType, type ReferencedNodeValue } from "@doitintl/cmp-models";
import { Stack, TextField } from "@mui/material";
import { useFormikContext } from "formik";
import * as yup from "yup";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { ReferencedFieldStandalone } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldStandalone";
import { type NodeWitOutputModel } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { createReferencedNodeSchema } from "../../../ApiActionParametersForm/useApiActionParametersSchema";
import { useFieldCommonProps } from "../../../ApiActionParametersForm/useFieldCommonProps";
import { FieldSectionHeader } from "../../../Common/FieldSectionHeader";
import { useTransformationNodeSchemaContext } from "./TransformationNodeForm";

const newFieldNameLabel = cloudflowTexts.NEW_FIELD_NAME_LABEL;

function generateFirstItemActionSchema(
  referenceableNodes: NodeWitOutputModel[],
  rootReferencedNodeValue: ReferencedNodeValue = {
    referencedField: [],
    referencedNodeId: "",
  }
) {
  return yup.object({
    type: yup.string().default(NodeTransformationType.FIRST_ITEM),
    newFieldName: yup.string().default("").required().label(newFieldNameLabel).trim(),
    payload: createReferencedNodeSchema(referenceableNodes, {
      requiredModelType: [ModelType.LIST],
    })
      .label("Referenced field")
      .test(
        "referenced-field-not-equal-to-itself",
        "Referenced field must not be equal to itself",
        (value) => !areReferencedNodeValuesEqual(value, rootReferencedNodeValue)
      ),
  });
}

export const FirstItemTransformationForm: FC = () => {
  const { setTransformationSchema, referenceableNodes } = useTransformationNodeSchemaContext();
  const { getFieldProps, setFieldValue } = useFormikContext();

  const { value: rootReferencedNodeValue } = getFieldProps("referencedNodeField");

  const [validationSchema, setValidationSchema] = useState<yup.AnyObjectSchema>(
    generateFirstItemActionSchema(referenceableNodes, rootReferencedNodeValue)
  );

  const [dataSourceNodes, setDataSourceNodes] = useState<NodeWitOutputModel[]>(
    referenceableNodes.filter(({ id }) => rootReferencedNodeValue.referencedNodeId === id)
  );

  const { value: transformationFieldValue } = getFieldProps("transformation");

  const newFieldNameCommonProps = useFieldCommonProps(
    getFieldProps("transformation.newFieldName"),
    newFieldNameLabel,
    true
  );
  const payloadCommonProps = useFieldCommonProps(
    getFieldProps("transformation.payload"),
    cloudflowTexts.REFERENCED_VALUE_LABEL,
    true
  );

  useEffect(() => {
    setTransformationSchema(validationSchema);
  }, [setTransformationSchema, validationSchema]);

  useEffect(() => {
    setValidationSchema(generateFirstItemActionSchema(referenceableNodes, rootReferencedNodeValue));
  }, [referenceableNodes, rootReferencedNodeValue]);

  useEffect(() => {
    if (!transformationFieldValue) {
      setFieldValue("transformation", validationSchema.getDefault());
    }
  }, [setFieldValue, validationSchema, transformationFieldValue]);

  useEffect(() => {
    setDataSourceNodes(referenceableNodes.filter(({ id }) => rootReferencedNodeValue.referencedNodeId === id));
  }, [referenceableNodes, rootReferencedNodeValue.referencedNodeId]);

  const isReferencedFieldDisabled = dataSourceNodes.length === 0;

  return (
    <Stack
      spacing={2}
      sx={{
        pb: 4,
      }}
    >
      <TextField fullWidth variant="outlined" size="small" {...newFieldNameCommonProps} />
      <FieldSectionHeader
        title={cloudflowTexts.FIRST_ITEM_TRANSFORMATION_TITLE}
        subtitle={cloudflowTexts.FIRST_ITEM_TRANSFORMATION_SUBTITLE}
      />
      <ReferencedFieldStandalone
        {...payloadCommonProps}
        referenceableNodes={dataSourceNodes}
        disabled={isReferencedFieldDisabled}
        tooltip={isReferencedFieldDisabled ? cloudflowTexts.SELECT_VALUE_FROM_DATA_SOURCE : ""}
        rootReferencedNodeValue={rootReferencedNodeValue}
      />
    </Stack>
  );
};
